import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { empty } from 'rxjs/observable/empty';
import { merge } from 'rxjs/observable/merge';
import { never } from 'rxjs/observable/never';
import { Observer } from 'rxjs/Observer';
import { take } from 'rxjs/operators/take';
import { takeUntil } from 'rxjs/operators/takeUntil';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { IScheduler } from 'rxjs/Scheduler';
import { AsyncAction } from 'rxjs/scheduler/AsyncAction';
import { AsyncScheduler } from 'rxjs/scheduler/AsyncScheduler';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';

import { Consumer } from './../consumers/consumer';
import { Generator } from './../generators/generator';
import { cancelExecuteLater, executeLater } from './../idle/idle';
import { AnyJson } from './../json/json.utils';
import { shareLast } from './../operators/operators';
import { isArray, isNotNil } from './../predicates/predicates';

/* Copyright IBM Corp. 2018 */
/**
 * Binds to the next function of a subject
 *
 * @param aSubject  the subject that handles the events
 *
 * @return the binding function
 */
function _createConsumer<T>(aSubject: Subject<T> | Observer<T>): Consumer<T> {
  return aSubject.next.bind(aSubject);
}

/**
 * Constructs a setter description
 *
 * @param aSubject  the subject that handles the setter events
 * @return the description of the setter
 */
function _createSetter<T>(aSubject: Subject<T>): PropertyDescriptor {
  // the descriptor
  return {
    'set': _createConsumer(aSubject),
    'enumerable': true
  };
}

/**
 * Constructs a getter description
 *
 * @param aObservable  the observable that handles the getter events
 * @param aInitial a potential initial value
 * @return the description of the getter
 */
function _createGetter<T>(aObservable: Observable<T>, aInitial?: T): PropertyDescriptor {
  // cache the value
  const value: BehaviorSubject<T> = new BehaviorSubject<T>(aInitial);
  // subscribe
  aObservable.subscribe(value);
  // the descriptor
  return {
    'get': value.getValue.bind(value),
    'enumerable': true
  };
}

class IdleFrameScheduler extends AsyncScheduler {

  public flush(action?: AsyncAction<any>): void {

    this.active = true;
    this.scheduled = undefined;

    const { actions } = this;
    let error: any;
    let index = -1;
    const count = actions.length;
    action = action || actions.shift();

    do {
      if (error = action.execute(action.state, action.delay)) {
        break;
      }
    } while (++index < count && (action = actions.shift()));

    this.active = false;

    if (error) {
      while (++index < count && (action = actions.shift())) {
        action.unsubscribe();
      }
      throw error;
    }
  }
}

class IdleFrameAction<T> extends AsyncAction<T> {

  constructor(protected scheduler: IdleFrameScheduler,
    protected work: (this: IdleFrameAction<T>, state?: T) => void) {
    super(scheduler, work);
  }

  protected requestAsyncId(scheduler: IdleFrameScheduler, id?: any, delay: number = 0): any {
    // If delay is greater than 0, request as an async action.
    if (delay !== null && delay > 0) {
      return super.requestAsyncId(scheduler, id, delay);
    }
    // Push the action to the end of the scheduler queue.
    scheduler.actions.push(this);
    // If an animation frame has already been requested, don't request another
    // one. If an animation frame hasn't been requested yet, request one. Return
    // the current animation frame request id.
    return scheduler.scheduled || (scheduler.scheduled = executeLater(
      () => scheduler.flush(null)));
  }
  protected recycleAsyncId(scheduler: IdleFrameScheduler, id?: any, delay: number = 0): any {
    // If delay exists and is greater than 0, or if the delay is null (the
    // action wasn't rescheduled) but was originally scheduled as an async
    // action, then recycle as an async action.
    if ((delay !== null && delay > 0) || (delay === null && this.delay > 0)) {
      return super.recycleAsyncId(scheduler, id, delay);
    }
    // If the scheduler queue is empty, cancel the requested animation frame and
    // set the scheduled flag to undefined so the next AnimationFrameAction will
    // request its own.
    if (scheduler.actions.length === 0) {
      cancelExecuteLater(id);
      scheduler.scheduled = undefined;
    }
    // Return undefined so the action knows to request a new async id if it's rescheduled.
    return undefined;
  }
}

const idleFrameScheduler: IScheduler = new IdleFrameScheduler(IdleFrameAction);

/*
 * Combines two observables such that the events on the first one are used until the second
 * one starts to produce an event. From then on only the events on the second one
 * will be used and the first one canceled.
 *
 * @param aFirst    the first observable
 * @param aNext     the next one
 * @return the combined observable
 */
function _thisThenThat<T>(aFirst: Observable<T>, aNext: Observable<T>): Observable<T> {

  // share this one
  const next = aNext.pipe(shareLast());

  /**
   * Either we get at least one event from the next observable or we
   * never stop, because if we stopped for an empty next sequence, the takeUntil operator
   * would cancel the operation.
   */
  const stop = merge(next, never()).pipe(take<T>(1));

  // produce events from the first observable until we get the stop event
  const first = aFirst.pipe(takeUntil<T>(stop));

  // connect
  return merge(first, next);
}


/*
 * Combines two observables such that the events on the first one are used until the second
 * one starts to produce an event. From then on only the events on the second one
 * will be used and the first one canceled.
 *
 * @param aObservables    the observables to combine
 * @return the combined observable
 */
function _thisThenThats<T>(...aObservables: Observable<T>[]): Observable<T> {
  // sanity check
  return aObservables
    .reduce(_thisThenThat, empty());
}

/**
 * Constructs a singe replay subject
 *
 * @return the subject
 */
function _createSingleSubject<T>(): ReplaySubject<T> {
  return new ReplaySubject<T>(1);
}

/*
 * Creates an observable that exposes the result of the function
 * call if the result is defined. Otherwise this is an empty sequence.
 *
 * @param aGenerator the generator function
 * @return observable of the result
*/
function _generateItem<T>(aGenerator: Generator<T>): Observable<T> {
  // constuct
  return Observable.create((observer: Observer<T>) => {
    const gen = aGenerator();
    if (isNotNil(gen)) {
      observer.next(gen);
    }
    observer.complete();
  });
}

/*
 * The empty observable instance, we can reuse the same
 */
export const EMPTY_STRING_OBSERVABLE: Observable<string> = empty();

/*
 * The empty observable instance, we can reuse the same
 */
export const EMPTY_JSON_OBSERVABLE: Observable<AnyJson> = empty();

/* Unsubscribes if the subscription exists.
 *
 * @param aSubscription   the subscription
 */
function _safeUnsubscribe(aSubscription: Subscription | null | undefined): void {
  if (isNotNil(aSubscription)) {
      aSubscription.unsubscribe();
  }
}

/* Unsubscribes if the subscription exists.
*
* @param aSubscriptions   the subscriptions
*/
function _safeUnsubscribeAll(aSubscriptions: Subscription[] | null | undefined): void {
  if (isArray(aSubscriptions)) {
      // delete all subscriptions
      let len = aSubscriptions.length;
      while (len-- > 0) {
          _safeUnsubscribe(aSubscriptions[len]);
      }
  }
}

export {
  _thisThenThat as thisThenThat,
  _thisThenThats as thisThenThats,
  _createSingleSubject as createSingleSubject,
  _createSetter as createSetterOnSubject,
  _createConsumer as createConsumerOnSubject,
  _createGetter as createGetterOnObservable,
  _generateItem as generateItem,
  _safeUnsubscribe as safeUnsubscribe,
  _safeUnsubscribeAll as safeUnsubscribeAll,
  idleFrameScheduler
};
